import {Component, OnInit, ViewChild} from '@angular/core';
import {CategoryDtoModel} from '../shared/models/category.dto.model';
import {LoaderService} from '../shared/services/loader.service';
import {CategoryService} from '../shared/services/category.service';
import {DomSanitizer} from '@angular/platform-browser';
import {ErrorStateMatcher} from '@angular/material/core';
import {LocalizationService} from '../shared/services/localization.service';
import {UntypedFormBuilder, Validators} from '@angular/forms';
import {FileUpload} from 'primeng/fileupload';
import {FileService} from '../shared/services/file.service';
import {ConfirmationService, MessageService} from 'primeng/api';

@Component({
    selector: 'njoy-category',
    templateUrl: './category.component.html',
    styleUrls: ['./category.component.scss']
})
export class CategoryComponent implements OnInit {

    public categories: CategoryDtoModel[] = [];
    public category: CategoryDtoModel;
    public addNewCategoryDialog = false;
    public modalTitle = '';

    public categoryForm;
    public matcher = new ErrorStateMatcher();
    public isEmojiPickerVisible = false

    public emojiPickerLabels = {
        search: this.localizationService.translate('EMOJI_PICKER.SEARCH'),
        emojilist: this.localizationService.translate('EMOJI_PICKER.LIST'),
        notfound: this.localizationService.translate('EMOJI_PICKER.NOT_FOUND'),
        clear: this.localizationService.translate('EMOJI_PICKER.CLEAR'),
        categories: {
            search: this.localizationService.translate('EMOJI_PICKER.CATEGORY_SEARCH'),
            recent: this.localizationService.translate('EMOJI_PICKER.CATEGORY_RECENT'),
            people: this.localizationService.translate('EMOJI_PICKER.CATEGORY_PEOPLE'),
            nature: this.localizationService.translate('EMOJI_PICKER.CATEGORY_NATURE'),
            foods: this.localizationService.translate('EMOJI_PICKER.CATEGORY_FOODS'),
            activity: this.localizationService.translate('EMOJI_PICKER.CATEGORY_ACTIVITY'),
            places: this.localizationService.translate('EMOJI_PICKER.CATEGORY_PLACES'),
            objects: this.localizationService.translate('EMOJI_PICKER.CATEGORY_OBJECTS'),
            symbols: this.localizationService.translate('EMOJI_PICKER.CATEGORY_SYMBOLS'),
            flags: this.localizationService.translate('EMOJI_PICKER.CATEGORY_FLAGS'),
            custom: this.localizationService.translate('EMOJI_PICKER.CATEGORY_CUSTOM')
        },
        skintones: {
            1: 'Default Skin Tone',
            2: 'Light Skin Tone',
            3: 'Medium-Light Skin Tone',
            4: 'Medium Skin Tone',
            5: 'Medium-Dark Skin Tone',
            6: 'Dark Skin Tone',
        }
    }

    public uploadedFile;

    public displayImage = false;
    public displayEmoji = false;
    public noFileUploaded = false;
    public duplicatedLabelMessageError = false;

    @ViewChild('fileUpload')
    public fileUploader: FileUpload;

    public categoriesListToUpdate = [];


    constructor(private categoryService: CategoryService,
                private loaderService: LoaderService,
                private formBuilder: UntypedFormBuilder,
                private localizationService: LocalizationService,
                private fileService: FileService,
                private messageService: MessageService,
                private confirmationService: ConfirmationService,
                public sanitizer: DomSanitizer) {
    }

    ngOnInit(): void {
        this.findAllCategories();
    }

    public addNew(): void {
        this.displayEmoji = false;
        this.isEmojiPickerVisible = true;
        this.displayImage = false;
        this.noFileUploaded = false;
        this.duplicatedLabelMessageError = false;
        this.uploadedFile = undefined;
        this.modalTitle = this.localizationService.translate('CATEGORY.ADD_NEW_MODAL_TITLE');
        this.initEmptyForm();
        delete this.category.uuid;
        this.addNewCategoryDialog = true;
    }

    public increasePriority(categroy: CategoryDtoModel): void {
        const selectedIndex = this.categories.indexOf(categroy);
        if (!(selectedIndex === 0)) {
            categroy.priority--;
            this.categories[selectedIndex - 1].priority++;
            this.sortCategoriesByPriority();
        }
        this.addIfNotExists(this.categories[selectedIndex - 1]);
        this.addIfNotExists(this.categories[selectedIndex]);
    }

    public decreasePriority(categroy: CategoryDtoModel): void {
        const selectedIndex = this.categories.indexOf(categroy);
        if (!(selectedIndex === this.categories.length - 1)) {
            categroy.priority++;
            this.categories[selectedIndex + 1].priority--;
            this.sortCategoriesByPriority();
        }
        this.addIfNotExists(this.categories[selectedIndex + 1]);
        this.addIfNotExists(this.categories[selectedIndex]);
    }

    private sortCategoriesByPriority(): void {
        this.categories.sort((item1: CategoryDtoModel, item2: CategoryDtoModel) => item1.priority - item2.priority);
    }

    private findAllCategories(): void {
        this.loaderService.startLoading();
        this.categoryService.findAllCategories().subscribe((data: CategoryDtoModel[]) => {
                this.categories = data;
                this.setEmojiCorrectFormat();
                this.loaderService.stopLoading();
            }, ((error: any) => {
                this.loaderService.stopLoading();
                console.log(error);
            })
        );
    }

    private setEmojiCorrectFormat(): void {
        this.categories.forEach((category: CategoryDtoModel) => {
            category.oldLabel = category.label;
            if (category.emoji) {
                category.emoji = '&#' + category.emoji + ';';
            }
        });
    }

    private initEmptyForm() {
        this.category = new CategoryDtoModel();
        if (this.categories.length === 0) {
            this.category.priority = 0;
        } else {
            this.category.priority = this.categories[this.categories.length - 1].priority + 1;
        }
        this.categoryForm = this.formBuilder.group({
            label: ['', [
                Validators.required
            ]],
            backgroundImage: [''],
            emoji: ['&#128512;'],
            family: [false]
        });
    }


    get getControl() {
        return this.categoryForm.controls;
    }

    public addOrUpdateCategory(): void {
        this.duplicatedLabelMessageError = false;
        this.displayRequiredMessage();
        if (this.categoryForm.status === 'VALID') {
            this.loaderService.startLoading();
            if (this.uploadedFile) {
                this.fileService.uploadCategoryFile(this.uploadedFile).subscribe(
                    (path: string) => {
                        if (this.modalTitle === this.localizationService.translate('CATEGORY.ADD_NEW_MODAL_TITLE')) {
                            this.addNewCategory(path);
                        } else {
                            this.updateCategory(path);
                        }
                    }, (err: any) => {
                        this.loaderService.stopLoading();
                        console.log(err)
                    }
                );
            } else {
                this.updateCategory();
            }
        }
    }

    private displayRequiredMessage(): void {
        this.categoryForm.touched = true;
        this.noFileUploaded = false;
        const keys = Object.keys(this.categoryForm.controls);
        keys.forEach((element: any) => {
            this.categoryForm.controls[element].touched = true;
        });
        if (!this.uploadedFile) {
            this.noFileUploaded = true;
        }
    }

    public addEmoji(event: any) {
        this.getControl.emoji.value = '&#' + parseInt(event.emoji.unified, 16) + ';';
        this.isEmojiPickerVisible = false;
        this.displayEmoji = true;
    }

    public openEmojiPicker() {
        this.displayEmoji = false;
        this.isEmojiPickerVisible = !this.isEmojiPickerVisible;
    }

    public changeImageValue() {
        this.displayImage = false;
        setTimeout(() => this.fileUploader.advancedFileInput.nativeElement.click(), 100);
    }

    public onUpload(event) {
        this.noFileUploaded = false;
        this.uploadedFile = event.files[0];
    }

    public clearFile() {
        this.displayImage = true;
        this.uploadedFile = undefined;
    }

    public openUpdateModal(category: CategoryDtoModel): void {
        this.displayImage = true;
        this.displayEmoji = true;
        this.duplicatedLabelMessageError = false;
        this.modalTitle = this.localizationService.translate('CATEGORY.UPDATE_MODAL_TITLE');
        this.setFormValues(category);
        this.addNewCategoryDialog = true;
    }

    public confirmDeleteCategory(category: CategoryDtoModel): void {
        this.confirmationService.confirm({
            message: this.localizationService.translate('CATEGORY.DELETE_CONFIRMATION_MESSAGE') + category.label,
            accept: () => {
                this.deleteCategory(category.uuid);
            }
        });
    }

    private deleteCategory(uuid: string): void {
        this.categoryService.deleteCategory(uuid).subscribe(
            () => {
                this.loaderService.stopLoading();
                this.displayMessage('success', 'CLIENT.SUCCESS_SUMMARY', 'CATEGORY.DELETE_SUCCESS_DETAIL');
                this.findAllCategories();
            }, (err: any) => {
                console.log(err);
                this.loaderService.stopLoading();
                if (err.error && err.error.code && err.error.code === 4029) {
                    this.displayMessage('error', 'CATEGORY.DELETE_ERROR_HEADER', 'CATEGORY.DELETE_ERROR_DETAIL');
                }
            }
        );
    }

    private setFormValues(category: CategoryDtoModel): void {
        this.category = new CategoryDtoModel();
        this.category.oldLabel = category.oldLabel;
        this.category.defaultKeyValue = category.defaultKeyValue;
        this.category.uuid = category.uuid;
        this.category.priority = category.priority;
        this.categoryForm = this.formBuilder.group({
            label: [category.label, [
                Validators.required
            ]],
            backgroundImage: [category.backgroundImagePath],
            emoji: [category.emoji],
            family: [category.family]
        });

    }

    private setValues(update: boolean): void {
        this.category.label = this.categoryForm.value.label;
        this.category.emoji = this.getControl.emoji.value.substring(2, this.getControl.emoji.value.length - 1);
        this.category.family = this.getControl.family.value;
        if (update && (this.category.oldLabel === this.category.label)) {
            this.category.label = this.category.defaultKeyValue;
        }
    }

    private addNewCategory(path: string): void {
        this.category = new CategoryDtoModel();
        this.setValues(false);
        this.category.backgroundImagePath = path;
        this.categoryService.createCategory(this.category).subscribe(() => {
            this.displayMessage('success', 'CLIENT.SUCCESS_SUMMARY', 'CATEGORY.ADD_SUCCESS_DETAIL');
            this.loaderService.stopLoading();
            this.addNewCategoryDialog = false;
            this.findAllCategories();
        }, (err: any) => {
            console.log(err);
            this.loaderService.stopLoading();
            if (err.error && err.error.code && err.error.code === 4026) {
                this.duplicatedLabelMessageError = true;
            }
        });
    }

    private updateCategory(path?: string): void {
        if (!this.noFileUploaded || (this.noFileUploaded && this.displayImage)) {
            this.setValues(true);
            this.category.backgroundImagePath = path ? path : this.categoryForm.value.backgroundImage;
            this.categoryService.updateCategory(this.category).subscribe(() => {
                if (this.modalTitle === this.localizationService.translate('CATEGORY.UPDATE_MODAL_TITLE')) {
                    this.displayMessage('success', 'CLIENT.SUCCESS_SUMMARY', 'CATEGORY.UPDATE_SUCCESS_DETAIL');
                } else {
                    this.displayMessage('success', 'CLIENT.SUCCESS_SUMMARY', 'CATEGORY.ADD_SUCCESS_DETAIL');
                }
                this.loaderService.stopLoading();
                this.addNewCategoryDialog = false;
                this.findAllCategories();
            }, (err: any) => {
                console.log(err);
                this.loaderService.stopLoading();
                if (err.error && err.error.code && err.error.code === 4026) {
                    this.duplicatedLabelMessageError = true;
                }
            });
        } else {
            this.loaderService.stopLoading();
        }
    }

    private displayMessage(severity: string, summary: string, detail: string): void {
        this.messageService.add({
            severity: severity,
            summary: this.localizationService.translate(summary),
            detail: this.localizationService.translate(detail)
        });
    }

    public updateListCategories(): void {
        this.loaderService.startLoading();
        this.categoryService.updateListCategories(this.categoriesListToUpdate).subscribe(() => {
            this.categoriesListToUpdate = [];
            this.loaderService.stopLoading();
        }, (err: any) => {
            console.log(err);
            this.loaderService.stopLoading();
        });
    }

    private addIfNotExists(category: CategoryDtoModel): void {
        const index = this.categoriesListToUpdate.findIndex(value => value.uuid === category.uuid);
        if (index === -1) {
            this.categoriesListToUpdate.push(category);
        } else {
            // splice and push
            this.categoriesListToUpdate.splice(index, 1);
            this.categoriesListToUpdate.push(category);
        }
    }
}
